/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, switchMap } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { FormView, Submission } from '../models';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormControl, FormGroup } from '@angular/forms';
import { forkJoin, iif, of, Subject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { questionGroupComponents } from '../creators';
import { ConfigService } from '@xilo-mono/common/config';
import { get, isArray, isEmpty, isPlainObject, set } from 'lodash';
import { JsonbType } from './integration-mapping.service';
import { EventLogCreateDTO } from '../constants/event.const';
export class FormState {
  valid: boolean;
  hideSections?: string[];
}

export interface IndexSummary {
  sectionIndex: number;
  subsectionIndex: number;
  questionIndex: number;
}

export type QuestionGroupAnimationState = 'unloaded' | 'moveup' | 'movedown' | 'appear';

@Injectable()
export class FormViewService {
  apiUrl = `${this.configService.apiUrl}/form-view`;
  zillowUrl = `${this.configService.apiUrl}/zillow`;
  workflowsUrl = `${this.configService.workflowsApiUrl}`;
  eventsApiUrl = `${this.configService.eventsApiUrl}`;
  formState = new BehaviorSubject<{ form: FormGroup; valid: boolean }>({
    form: null,
    valid: false
  });
  questionGroupState = new BehaviorSubject<{ valid: boolean }>({
    valid: false
  });
  submitForm = new BehaviorSubject<boolean>(false);
  updateForm = new BehaviorSubject<boolean>(false);
  assignAgent = new BehaviorSubject<boolean>(false);
  nextForm = new BehaviorSubject<boolean>(false);
  nextFormSection = new BehaviorSubject<number>(null);
  openBlockDialog = new BehaviorSubject<boolean>(false);
  scrollState = new BehaviorSubject<string>(null);
  loadState = new BehaviorSubject<string>(null);
  rebuildForm = new BehaviorSubject<boolean>(null);
  dataSource = new BehaviorSubject<any>({});
  metadata = new BehaviorSubject<any>(null);
  activeState = new BehaviorSubject<string>(null);
  researchPanelState = new BehaviorSubject<{ openPanel: boolean; address?: any }>({ openPanel: false });
  eventFired = new BehaviorSubject<'Visited XILO' | 'Started Form' | 'New Lead' | 'Finished Form' | null>(null);
  data = this.dataSource.asObservable();
  private _submitted = new Subject();
  readonly submitted$ = this._submitted.asObservable();
  sectionLoaded$ = new BehaviorSubject<number>(0);
  saveChanges$ = new Subject();
  formDirty$ = new BehaviorSubject(false);
  formViews = new BehaviorSubject<FormView[]>([]);
  testEventData = new BehaviorSubject<any>({
    state: null,
    testEvents: null,
    logs: null
  });
  intakeSummaryState = new BehaviorSubject({
    isClosed: false
  });
  addressV2 = new BehaviorSubject({
    streetAddress2: '',
    city: '',
    state: '',
    zipCode: null,
    canEdit: false
  });
  addressSubject: BehaviorSubject<{
    dataKey: string;
    value: string;
    googleEvent: {
      address_components: any[];
      formatted_address: any;
    };
  }> = new BehaviorSubject({
    dataKey: '',
    value: '',
    googleEvent: {
      address_components: [],
      formatted_address: ''
    }
  });
  intakeAnotherSubject = new BehaviorSubject(false);
  intakeAnother$ = this.intakeAnotherSubject.asObservable();
  token = localStorage.getItem('token');
  options = {
    headers: { 'x-access-token': this.token }
  };
  private sectionsLoaded = 0;
  private openAllSectionsFlag = false;
  formView: FormView;
  fields: FormlyFieldConfig[] = [];
  model: any;
  form = new FormGroup({});
  submission: Submission;
  formSubmitted$ = new Subject();
  fieldIdsWithErrorInIntakeForm$ = new BehaviorSubject<Set<string>>(null);
  submitted = false;
  openedAccordions$ = new BehaviorSubject<Set<string>>(new Set());
  constructor(private http: HttpClient, private configService: ConfigService) {}

  setOpenAllSections(value: boolean) {
    this.openAllSectionsFlag = value;
  }
  
  shouldOpenAllSections(): boolean {
    return this.openAllSectionsFlag;
  }

  updateOpenedAccordions(panelId: string, isOpen: boolean) {
    const current = new Set(this.openedAccordions$.getValue());
    isOpen ? current.add(panelId) : current.delete(panelId);
    this.openedAccordions$.next(current);
  }

  mergeAccordionsToOpen(newSet: Set<string>) {
    const current = new Set(this.openedAccordions$.getValue());
    newSet.forEach(id => current.add(id));
    this.openedAccordions$.next(current);
  }

  listZillowData(address: any) {
    return this.http.post(`${this.zillowUrl}/get-link`, address).pipe(map(result => result['obj']));
  }

  changeFormState(formState: { form: FormGroup; valid: boolean }) {
    this.formState.next(formState);
  }

  changeQuestionGroupState(formState: FormState) {
    this.questionGroupState.next(formState);
  }

  changeRebuildForm(rebuild: boolean) {
    this.rebuildForm.next(rebuild);
  }

  scrollIntoView(key: string) {
    this.scrollState.next(key);
  }

  changeLoadState(state: string) {
    this.loadState.next(state);
  }

  // This call should be made every time the user submits the form
  setFormAsSubmitted() {
    this._submitted.next(true);
  }

  onSubmitForm(submit: boolean) {
    this.submitForm.next(submit);
  }

  onSaveChanges() {
    this.saveChanges$.next(true);
  }

  changeFormDirty(dirty: boolean) {
    this.formDirty$.next(dirty);
  }

  onNextForm(next: boolean) {
    this.nextForm.next(next);
  }

  onNextFormBySection(section: number) {
    this.nextFormSection.next(section);
  }

  onOpenBlockDialog(open: boolean) {
    this.openBlockDialog.next(open);
  }

  changeSectionActiveView(key: any) {
    this.activeState.next(key);
  }

  create(formData: any) {
    return this.http.post(`${this.apiUrl}/create`, formData).pipe(map(result => result['obj']));
  }

  duplicateByKey(companyId: number, formId: number, newMetadata?: any, newComponents?: any) {
    const body = {
      newMetadata,
      newComponents
    };
    return this.http.post(`${this.apiUrl}/duplicate-form-by-key/${companyId}/${formId}`, body).pipe(map(result => result['obj']));
  }

  duplicateByTemplateParams(companyId: number, formTemplateParams: any, newFormMetaParams?: any) {
    const body = {
      formTemplateParams,
      newFormMetaParams
    };
    return this.http.post(`${this.apiUrl}/duplicate-form-by-template-params/${companyId}`, body);
  }

  update(formData: any) {
    return this.http.put(`${this.apiUrl}/update`, formData).pipe(map(result => result['obj']));
  }

  testForm(formKey: string, companyId: number) {
    this.token = localStorage.getItem('token');
    this.options = {
      headers: { 'x-access-token': this.token }
    };
    return this.http.get(`${this.apiUrl}/test-form-view/${formKey}/${companyId}`, this.options);
  }

  getForm(id: string, version = 'latest', submissionId = null, companyId: string) {
    let v = version;
    if (!v) {
      v = 'latest';
    }
    const query = submissionId ? `&submissionId=${submissionId}` : '';
    return this.http.get(`${this.apiUrl}/list-one/${companyId}/${id}?version=${v}${query}`).pipe(map(result => result['obj']));
  }

  getFormByKeyAndCompanyId(key: string, companyId: number) {
    return this.http.get(`${this.apiUrl}/company/${companyId}/form/${key}`).pipe(map(result => result['obj']));
  }

  getFormById(id: string | number) {
    return this.http.get(`${this.apiUrl}/list-one-by-id/${id}`).pipe(map(result => result['obj']));
  }

  getTemplates() {
    return this.http.get(`${this.apiUrl}/list-templates`).pipe(
      map(result => result['obj']),
      switchMap((templates: FormView[]) => {
        return iif(
          () => !templates.length || templates.length === 0,
          forkJoin([this.http.get('/assets/data/intake-form-data.json').pipe(map(result => result['obj'])), this.http.get('/assets/data/customer-form-data.json').pipe(map(result => result['obj']))]),
          of(templates)
        );
      })
    );
  }

  getFormByFormId(id: string | number) {
    return this.http.get(`${this.apiUrl}/list-one-by-form/${id}`).pipe(map(result => result['obj']));
  }

  /**
   * @deprecated getFormsByCompany
   * @returns a collection of forms FormView[]
   * All instances of getFormsByCompany() found in the components
   * instance as of today has the mapping logic in the pipeline.
   * forms.service.ts houses the final Observable which
   * will later be replaced after adopting feature
   * module of @module FormsModule
   */
  getFormsByCompany(formGroup?: string): Observable<FormView[]> {
    const query = formGroup ? `?formGroup=${formGroup}` : '';
    return this.http.get(`${this.apiUrl}/list-all-by-company${query}`).pipe(
      map((result: { title: string; obj: FormView[] }) => {
        this.formViews.next(result.obj);
        return result.obj;
      })
    );
  }

  getPublicForms(companyId: number) {
    return this.http.get(`${this.apiUrl}/public-forms/${companyId}`).pipe(map(res => res['obj']));
  }

  getFormsByGroup(companyId: number, formGroup: any) {
    const query = `?companyId=${companyId}&formGroup=${formGroup}`;
    return this.http.get(`${this.apiUrl}/list-all-by-group${query}`).pipe(map(res => res['obj']));
  }

  delete(id: string | number) {
    return this.http.delete(`${this.apiUrl}/delete/${id}`).pipe(map(result => result['obj']));
  }

  sectionLoaded() {
    this.sectionsLoaded++;
    this.sectionLoaded$.next(this.sectionsLoaded);
  }

  getSection(sectionIndex: number) {
    if (this.fields) {
      return this.fields[sectionIndex];
    } else {
      return null;
    }
  }

  isRepeatField(field: FormlyFieldConfig) {
    return field.fieldArray && field.fieldArray?.fieldGroup?.length > 0;
  }

  hasRepeatAnswers(section: FormlyFieldConfig) {
    return section.fieldArray && section.fieldGroup?.length > 1;
  }

  repeatCount(sectionIndex: number) {
    const section = this.getSection(sectionIndex);
    if (section && this.isRepeatField(section) && this.model) {
      const sectionValue = this.model[section.key as string];
      return sectionValue?.length || 1;
    }
    return 1;
  }

  getQuestionsForSection(sectionIndex: number) {
    const section = this.getSection(sectionIndex);
    if (section) {
      return section.fieldArray ? section.fieldArray.fieldGroup : section.fieldGroup;
    } else {
      return null;
    }
  }

  getQuestion(sectionIndex: number, questionIndex: number) {
    const questions = this.getQuestionsForSection(sectionIndex);
    if (questions) {
      return questions[questionIndex];
    } else {
      return null;
    }
  }

  getPrevious(current: IndexSummary) {
    let previous = this.getPreviousIndexes(current);
    while (this.isHidden(previous)) {
      previous = this.getPreviousIndexes(previous);
      if (previous.sectionIndex < 0) {
        previous = { sectionIndex: 0, subsectionIndex: 0, questionIndex: 0 };
        break;
      }
    }
    return previous;
  }

  getPreviousIndexes(current: IndexSummary) {
    let previous: IndexSummary = {
      sectionIndex: 0,
      subsectionIndex: 0,
      questionIndex: 0
    };
    const section = this.getSection(current.sectionIndex);
    if (section) {
      const hasRepeatAnswers = this.hasRepeatAnswers(section);
      const questions = this.getQuestionsForSection(current.sectionIndex);
      if (hasRepeatAnswers && current.subsectionIndex > 0) {
        if (current.questionIndex === 0) {
          previous = {
            sectionIndex: current.sectionIndex,
            subsectionIndex: current.subsectionIndex - 1,
            questionIndex: questions.length - 1
          };
        } else {
          previous = {
            sectionIndex: current.sectionIndex,
            subsectionIndex: current.subsectionIndex,
            questionIndex: current.questionIndex - 1
          };
        }
      } else {
        if (current.questionIndex === 0) {
          const previousSection = this.getSection(current.sectionIndex - 1);
          const previousQuestions = this.getQuestionsForSection(current.sectionIndex - 1);
          const previousHasRepeatAnswers = this.hasRepeatAnswers(previousSection);
          previous = {
            sectionIndex: current.sectionIndex - 1,
            subsectionIndex: previousHasRepeatAnswers ? previousSection.fieldGroup.length - 1 : 0,
            questionIndex: previousQuestions.length - 1
          };
        } else {
          previous = {
            sectionIndex: current.sectionIndex,
            subsectionIndex: current.subsectionIndex,
            questionIndex: current.questionIndex - 1
          };
        }
      }
    }
    return previous;
  }

  getNext(current: IndexSummary) {
    if (this.isLast(current)) {
      return current;
    }
    let next = this.getNextIndexes(current);
    while (this.isHidden(next)) {
      next = this.getNextIndexes(next);
      if (next.sectionIndex >= this.fields?.length) {
        next = {
          sectionIndex: 0,
          subsectionIndex: 0,
          questionIndex: 0
        };
        break;
      }
    }
    return next;
  }

  getNextSection(current: IndexSummary) {
    if (this.isLast(current)) {
      return current;
    }
    const next = {
      sectionIndex: current.sectionIndex + 1,
      subsectionIndex: 0,
      questionIndex: 0
    };
    while (this.isHidden(next)) {
      next.sectionIndex += 1;
    }
    return next;
  }

  getNextIndexes(current: IndexSummary) {
    let next: IndexSummary = {
      sectionIndex: 0,
      subsectionIndex: 0,
      questionIndex: 0
    };
    const section = this.getSection(current.sectionIndex);
    if (section) {
      const hasRepeatAnswers = section?.fieldArray && section?.fieldGroup?.length > 1;
      const questions = this.getQuestionsForSection(current.sectionIndex);
      if (questions) {
        if (hasRepeatAnswers && current.subsectionIndex < section.fieldGroup?.length - 1) {
          next =
            current.questionIndex < questions.length - 1
              ? {
                  sectionIndex: current.sectionIndex,
                  subsectionIndex: current.subsectionIndex,
                  questionIndex: current.questionIndex + 1
                }
              : {
                  sectionIndex: current.sectionIndex,
                  subsectionIndex: current.subsectionIndex + 1,
                  questionIndex: 0
                };
        } else {
          next =
            current.questionIndex < questions.length - 1
              ? {
                  sectionIndex: current.sectionIndex,
                  subsectionIndex: current.subsectionIndex,
                  questionIndex: current.questionIndex + 1
                }
              : {
                  sectionIndex: current.sectionIndex + 1,
                  subsectionIndex: 0,
                  questionIndex: 0
                };
        }
      }
    }
    return next;
  }

  isLast(summary: IndexSummary) {
    const lastIndex = this.getLastIndex();
    return summary.sectionIndex === lastIndex.sectionIndex && summary.subsectionIndex === lastIndex.subsectionIndex && summary.questionIndex === lastIndex.questionIndex;
  }

  getLastIndex() {
    const lastSectionIndex = this.fields.length - 1;
    const lastSection = this.getSection(lastSectionIndex);
    const lastQuestionIndex = this.getQuestionsForSection(lastSectionIndex)?.length - 1;
    const lastSubsectionIndex = this.hasRepeatAnswers(lastSection) ? lastSection.fieldGroup.length - 1 : 0;
    let index: IndexSummary = {
      sectionIndex: lastSectionIndex,
      subsectionIndex: lastSubsectionIndex,
      questionIndex: lastQuestionIndex
    };
    while (this.isHidden(index)) {
      index = this.getPreviousIndexes(index);
      if (index.sectionIndex < 0 || index.subsectionIndex < 0 || index.questionIndex < 0) {
        index = {
          sectionIndex: 0,
          subsectionIndex: 0,
          questionIndex: 0
        };
        break;
      }
    }
    return index;
  }

  isHidden(summary: IndexSummary) {
    const section = this.getSection(summary.sectionIndex);
    if (section) {
      const sectionHidden = section.templateOptions.hidden;
      if (sectionHidden) {
        return true;
      } else if (summary.questionIndex >= 0) {
        const question = this.getQuestion(summary.sectionIndex, summary.questionIndex);
        if (question && typeof question.hideExpression === 'function') {
          return question.hideExpression(this.model, summary, question);
        } else {
          return question.templateOptions?.hidden;
        }
      }
    }
    return false;
  }

  hideExpression(model: any, formState: any, field: FormlyFieldConfig) {
    if (field?.templateOptions?.query) {
      field.templateOptions.hidden = true;
      return true;
    } else {
      field.templateOptions.hidden = false;
      return false;
    }
  }

  getProgress(): IndexSummary {
    let sectionIndex = 0;
    let subsectionIndex = 0;
    let questionIndex = 0;
    while (sectionIndex < this.fields.length) {
      if (!this.isHidden({ sectionIndex, subsectionIndex, questionIndex })) {
        const section = this.getSection(sectionIndex);
        if (this.isRepeatField(section)) {
          while (subsectionIndex === 0 || subsectionIndex < section.fieldGroup.length) {
            const questions = this.getQuestionsForSection(sectionIndex);
            while (questionIndex < questions.length) {
              if (!this.isHidden({ sectionIndex, questionIndex, subsectionIndex })) {
                const question = questions[questionIndex];
                const control = this.getControl(section.key as string, question.key as string, subsectionIndex);
                if (!control?.valid) {
                  return { sectionIndex, subsectionIndex, questionIndex };
                }
              }
              questionIndex++;
            }
            subsectionIndex++;
            questionIndex = 0;
          }
        } else {
          subsectionIndex = 0;
          const questions = this.getQuestionsForSection(sectionIndex);
          while (questionIndex < questions.length) {
            const question = questions[questionIndex];
            const control = this.getControl(section.key as string, question.key as string);
            if (!control?.valid) {
              return { sectionIndex, subsectionIndex, questionIndex };
            }
            questionIndex++;
          }
        }
      }
      sectionIndex++;
      subsectionIndex = 0;
      questionIndex = 0;
    }
    return { sectionIndex, subsectionIndex, questionIndex };
  }

  getControl(sectionKey: string, questionKey: string, subsectionIndex?: number) {
    if (this.form?.controls) {
      const sectionControl = this.form.controls[sectionKey];
      if (sectionControl) {
        if (!isNaN(subsectionIndex) && Array.isArray((sectionControl as FormGroup).controls)) {
          const subsectionControl = (sectionControl as FormGroup).controls[subsectionIndex];
          if (subsectionControl) {
            const questionControl = (subsectionControl as FormGroup).controls[questionKey];
            return questionControl;
          } else {
            return null;
          }
        } else {
          const questionControl = (sectionControl as FormGroup).controls[questionKey];
          return questionControl;
        }
      }
      return sectionControl;
    } else {
      return null;
    }
  }

  getFieldIndex(field: FormlyFieldConfig) {
    if (isNaN(+field.key)) {
      return this.fields.findIndex(section => section.key === field.key);
    } else {
      return this.fields.findIndex(section => section.fieldArray?.fieldGroup[0].key === field.fieldGroup[0].key);
    }
  }

  convertForm(form: any, convertToCustomerForm: boolean) {
    if (form?.components?.length > 0) {
      form.components[0].type = convertToCustomerForm ? 'customer-form' : 'intake-form';
      const sections: FormlyFieldConfig[] = form.components[0].fieldGroup;
      sections.forEach(section => {
        this.convertSection(section, convertToCustomerForm);
      });
      if (convertToCustomerForm) {
        this.checkRepeatSections(form.components[0]);
      }
      const parentFieldDictionary = {};
      this.buildFieldParentsDictionary(sections, [], parentFieldDictionary);
      this.updateConditionalLogic(form.components, parentFieldDictionary);
    }
  }

  convertSection(section: FormlyFieldConfig, convertToCustomerForm: boolean) {
    if (!convertToCustomerForm) {
      // do this before other conversions so we're dealing with a flat question list
      this.flattenQuestionGroup(section);
    }
    if (this.isRepeatField(section)) {
      section.type = convertToCustomerForm ? 'customer-repeat' : 'intake-repeat';
      section.fieldArray.type = convertToCustomerForm ? 'customer-section' : 'intake-section';
      section.fieldArray.wrappers = convertToCustomerForm ? ['customer-section-panel'] : ['section-panel'];
      section.fieldArray.fieldGroup.forEach(question => {
        this.convertField(question, convertToCustomerForm);
      });
    } else {
      section.type = convertToCustomerForm ? 'customer-section' : 'intake-section';
      section.wrappers = convertToCustomerForm ? ['customer-section-panel'] : ['section-panel'];
      section.fieldGroup.forEach(question => this.convertField(question, convertToCustomerForm));
    }
    if (convertToCustomerForm) {
      this.createQuestionGroup(section);
    }
  }

  convertField(field: FormlyFieldConfig, convertToCustomerForm: boolean) {
    if (convertToCustomerForm) {
      if (field.templateOptions.className === 'intake-radio' || field.templateOptions.className?.includes('radio')) {
        field.templateOptions.className = 'customer-radio';
      } else {
        field.templateOptions.className = 'xilo-input';
      }
    } else {
      if (field.templateOptions.className === 'customer-radio' || field.templateOptions.className?.includes('radio')) {
        field.templateOptions.className = 'intake-radio';
      } else {
        field.templateOptions.className = 'intake-input';
      }
      if (field.type === 'selectbox') {
        field.type = 'select';
      }
    }
  }

  flattenQuestionGroup(section: FormlyFieldConfig) {
    let flattenedQuestions = [];
    const repeatField = this.isRepeatField(section);
    const questionGroups = repeatField ? section.fieldArray.fieldGroup : section.fieldGroup;
    questionGroups.forEach(questionGroup => {
      if (!questionGroup.templateOptions.repeatAddAnother && questionGroup.fieldGroup?.length > 0) {
        if (questionGroup.templateOptions?.query?.rules?.length > 0) {
          questionGroup.fieldGroup.forEach(question => {
            if (question.templateOptions?.query?.rules?.length > 0) {
              question.templateOptions.query = {
                condition: 'or',
                rules: [questionGroup.templateOptions.query, question.templateOptions.query],
                showWhenTrue: false
              };
            } else {
              question.templateOptions.query = questionGroup.templateOptions.query;
            }
          });
        }
        flattenedQuestions = flattenedQuestions.concat(questionGroup.fieldGroup);
      }
    });
    if (flattenedQuestions.length > 0) {
      if (repeatField) {
        section.fieldArray.fieldGroup = flattenedQuestions;
      } else {
        section.fieldGroup = flattenedQuestions;
      }
    }
  }

  createQuestionGroup(section: FormlyFieldConfig) {
    const repeatSection = this.isRepeatField(section);
    const questionGroup = {
      label: section['label'],
      type: 'question-group',
      wrappers: ['question-panel'],
      key: uuid(),
      templateOptions: {
        label: 'Please provide the following information'
      },
      fieldGroup: repeatSection ? section.fieldArray.fieldGroup : section.fieldGroup
    };
    if (repeatSection) {
      section.fieldArray.fieldGroup = [questionGroup];
    } else {
      section.fieldGroup = [questionGroup];
    }
  }

  checkRepeatSections(form: FormlyFieldConfig) {
    form.fieldGroup.forEach((section: FormlyFieldConfig) => {
      if (section.type === 'customer-repeat') {
        const addAnotherQuestionGroup = section.fieldArray.fieldGroup.find(questionGroup => questionGroup?.templateOptions?.repeatAddAnother);
        if (!addAnotherQuestionGroup) {
          const label = section.templateOptions?.label?.toLowerCase();
          const repeatAddAnother = questionGroupComponents.repeatAddAnother(label);
          section.fieldArray.fieldGroup.push(repeatAddAnother);
        } else {
          const selectbox = addAnotherQuestionGroup.fieldGroup[0];
          let options = selectbox?.templateOptions?.options as any[];
          if (options?.length !== 2) {
            options = options.slice(0, 2);
          }
          options[0].value = true;
          options[1].value = false;
        }
      }
    });
  }

  buildFieldParentsDictionary(fieldGroup: FormlyFieldConfig[], parents: string[] = [], dictionary: { [key: string]: string[] }) {
    fieldGroup.forEach(field => {
      if (field.fieldArray?.fieldGroup?.length > 0) {
        // Repeat form type
        this.buildFieldParentsDictionary(field.fieldArray.fieldGroup, [...parents, field.key as string], dictionary);
      } else if (field.fieldGroup?.length > 0) {
        // Section form type
        this.buildFieldParentsDictionary(field.fieldGroup, [...parents, field.key as string], dictionary);
      } else {
        dictionary[field.key as string] = parents; // Actual element
      }
    });
  }

  updateConditionalLogic(fieldGroup: FormlyFieldConfig[], dictionary: { [key: string]: string[] }) {
    fieldGroup.forEach(field => {
      if (field.templateOptions?.query?.rules?.length > 0) {
        this.updateRuleParents(field.templateOptions.query.rules, dictionary);
      }
      if (field.fieldArray?.fieldGroup?.length > 0) {
        this.updateConditionalLogic(field.fieldArray.fieldGroup, dictionary);
      } else if (field?.fieldGroup?.length > 0) {
        this.updateConditionalLogic(field.fieldGroup, dictionary);
      }
    });
  }

  updateRuleParents(rules: any[], dictionary: { [key: string]: string[] }) {
    rules.forEach(rule => {
      if (rule.rules) {
        this.updateRuleParents(rule.rules, dictionary);
      } else {
        rule.parents = dictionary[rule.field];
      }
    });
  }

  questionReferencedInConditionalLogic(question: FormlyFieldConfig, fields: FormlyFieldConfig[]) {
    const questionReferenced = fields.some(field => {
      let referencedByField = false;
      if (field.templateOptions?.query?.rules?.length > 0) {
        referencedByField = field.templateOptions.query.rules.some(rule => rule.field === question.key);
      }
      if (referencedByField) {
        return true;
      } else {
        if (field.fieldArray?.fieldGroup?.length > 0) {
          return this.questionReferencedInConditionalLogic(question, field.fieldArray.fieldGroup);
        } else if (field.fieldGroup?.length > 0) {
          return this.questionReferencedInConditionalLogic(question, field.fieldGroup);
        } else {
          return false;
        }
      }
    });
    return questionReferenced;
  }

  removeConditionalLogicReferences(question: FormlyFieldConfig, fields: FormlyFieldConfig[]) {
    fields.forEach(field => {
      if (field.templateOptions?.query?.rules?.length > 0) {
        field.templateOptions.query.rules = field.templateOptions.query.rules.filter(rule => rule.field !== question.key);
        if (field.templateOptions.query.rules.length === 0) {
          field.templateOptions.query = null;
        }
      }
      if (field?.fieldArray?.fieldGroup?.length > 0) {
        this.removeConditionalLogicReferences(question, field.fieldArray.fieldGroup);
      } else if (field?.fieldGroup?.length > 0) {
        this.removeConditionalLogicReferences(question, field.fieldGroup);
      }
    });
  }

  /**
   * Flattens the nested FormControls in the object set by formly.
   * Assumes FormGroups are not nested more than one level deep
   * i.e., <formly-form [form]="form"></formly-form>
   * @param form:FormGroup
   * @return FormControl[]
   */
  flattenFormControls(form: FormGroup): FormControl[] {
    let allControls = [];
    const formArrays = form.controls || [];
    for (const formArray of Object.values(formArrays)) {
      const controls = get(formArray, 'controls');
      if (isArray(controls) && controls.length) {
        const formControls = controls.reduce((acc, curr, i) => {
          const ctrls = get(curr, 'controls');
          if (isPlainObject(ctrls) && !isEmpty(ctrls)) {
            const values = Object.values(ctrls).map((control: FormControl) => {
              set(control, '_fields[0].templateOptions.index', i);
              return control;
            });
            return [...acc, ...values];
          }
        }, []);
        allControls = [...allControls, ...formControls];
      } else if (isPlainObject(controls) && !isEmpty(controls)) {
        allControls = [...allControls, ...Object.values(controls)];
      }
    }
    return allControls;
  }

  filterFormControlsWithErrors(formControls: FormControl[]) {
    return formControls.filter((fc: FormControl) => fc.status === 'INVALID');
  }

  fetchDeletedForms(companyId: number) {
    return this.http.get(`${this.apiUrl}/company/${companyId}/system-admin/deleted-forms`);
  }

  restoreFormById(formId: number) {
    return this.http.patch(this.apiUrl + '/system-admin/' + 'restore-form/' + formId, {});
  }

  // This is a workflow endpoint that lives here due to it being used in the select menu question
  addInsurgridClient(companyId: string, clientId: number) {
    return this.http.post<JsonbType>(`${this.workflowsUrl}/company/${companyId}/insurGrid/create-client/${clientId}`, {});
  }

  logEvent(event: EventLogCreateDTO) {
    return this.http.post<JsonbType>(`${this.eventsApiUrl}/event-logs`, event);
  }

  getTrackedFields(field: FormlyFieldConfig): FormlyFieldConfig[] {
    return field?.fieldGroup?.filter(f =>
      f.type === 'firstName' ||
      f.type === 'lastName' ||
      f.templateOptions?.dataKey === 'vehicleYear' ||
      f.templateOptions?.dataKey === 'vehicleMake' ||
      f.templateOptions?.dataKey === 'vehicleModel'
    ) ?? [];
  }

  getSectionsList(form: any): { name: string; id: string }[] {
    const masterSections = form?.components?.[0]?.fieldGroup ?? []
    return masterSections
      .filter(
        (section: any) =>
          (section.type === 'intake-section' || section.type === 'intake-repeat') &&
          !!section.key
      )
      .map((section: any) => ({
        name: section.templateOptions?.label ?? 'Untitled',
        id: section.key
      }))
  }  

  get isMacUser(): boolean {
    const userAgentData = (navigator as any).userAgentData;
    if (userAgentData?.platform) {
      return userAgentData.platform.toLowerCase().includes('mac');
    } else if (navigator.platform) {
      return navigator.platform.toLowerCase().includes('mac');
    }
    return false;
  }
}
